// PRD.318 — PowerSync queries for Dental Treatment Plan & Treatment Done.
//
// Split from `dental-treatment-logic.ts` (lifecycle, selection, suggestions)
// because that module must stay import-free to run under the node:test harness.

import type { QuerySpec } from '@helios/powersync-client-core';

export * from './dental-treatment-logic.js';

/**
 * Every dental treatment plan item for a patient.
 *
 * PATIENT-scoped, not encounter-scoped: a plan raised in August is still active
 * at September's visit. Status filtering happens in `selectActivePlan`.
 */
export function qDentalTreatmentsByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['dental_treatments', 'by-patient', patientId] as const,
    sql: `SELECT * FROM dental_treatments WHERE patient_id = ?`,
    params: [patientId],
    watch: ['dental_treatments'],
  };
}

/** Planned tooth scope for all of a patient's treatments, grouped client-side. */
export function qDentalTreatmentTeethByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['dental_treatment_teeth', 'by-patient', patientId] as const,
    sql: `SELECT * FROM dental_treatment_teeth WHERE patient_id = ?`,
    params: [patientId],
    watch: ['dental_treatment_teeth'],
  };
}

/**
 * Every performed record for a patient, across encounters.
 *
 * Patient-wide so one plan row can show its full multi-visit history; the
 * Treatment Done view narrows to the active encounter in the UI.
 */
export function qDentalTreatmentRecordsByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['dental_treatment_records', 'by-patient', patientId] as const,
    sql: `SELECT * FROM dental_treatment_records WHERE patient_id = ?`,
    params: [patientId],
    watch: ['dental_treatment_records'],
  };
}

/** Performed tooth scope for all of a patient's records. */
export function qDentalTreatmentRecordTeethByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['dental_treatment_record_teeth', 'by-patient', patientId] as const,
    sql: `SELECT * FROM dental_treatment_record_teeth WHERE patient_id = ?`,
    params: [patientId],
    watch: ['dental_treatment_record_teeth'],
  };
}

/** One service-linked dental treatment Billing may add, with its tooth scope. */
export interface DentalTreatmentCandidateRow {
  /** Stable id of the originating clinical record — the invoice `source_id`. */
  source_id: string;
  /** 'plan' | 'performed' — which clinical table it came from. */
  source_kind: string;
  service_id: string;
  /** The SNAPSHOT label documented at the time, not the live service name. */
  treatment: string;
  /** Space-separated FDI numbers, or '' for a general treatment. */
  teeth: string | null;
  service_name: string | null;
  price_cents: number | null;
  status: string | null;
}

/**
 * Service-linked dental treatments for one patient, for the Billing picker.
 *
 * Mirrors `qInvoicePrescriptionCandidates`: the clinical module EXPOSES what is
 * eligible, Billing decides what to add and resolves pricing itself.
 *
 * Eligibility is `service_id IS NOT NULL` — free-text treatment is valid
 * documentation but has no catalogue item to bill, and is never fuzzy-matched.
 * Cancelled plans are excluded. Plan status is otherwise NOT filtered: clinics
 * bill before, during and after treatment.
 *
 * Patient-scoped rather than encounter-scoped because a plan is longitudinal —
 * work planned at an earlier visit is still billable today.
 *
 * `price_cents` is joined for DISPLAY in the picker only; the invoice line
 * takes its price through Billing's normal rules at the moment it is created.
 */
export function qDentalTreatmentCandidates(patientId: string): QuerySpec<[string, string]> {
  return {
    key: ['dental-treatment-candidates', patientId] as const,
    sql: `
      SELECT
        t.id            AS source_id,
        'plan'          AS source_kind,
        t.service_id    AS service_id,
        t.treatment     AS treatment,
        t.status        AS status,
        s.name          AS service_name,
        s.price_cents   AS price_cents,
        (SELECT group_concat(tt.tooth, ' ')
           FROM dental_treatment_teeth tt
          WHERE tt.dental_treatment_id = t.id) AS teeth
      FROM dental_treatments t
      LEFT JOIN appointment_services s ON s.id = t.service_id
      WHERE t.patient_id = ?
        AND t.service_id IS NOT NULL
        AND t.status != 'cancelled'

      UNION ALL

      SELECT
        r.id            AS source_id,
        'performed'     AS source_kind,
        r.service_id    AS service_id,
        r.treatment     AS treatment,
        NULL            AS status,
        s.name          AS service_name,
        s.price_cents   AS price_cents,
        (SELECT group_concat(rt.tooth, ' ')
           FROM dental_treatment_record_teeth rt
          WHERE rt.treatment_record_id = r.id) AS teeth
      FROM dental_treatment_records r
      LEFT JOIN appointment_services s ON s.id = r.service_id
      WHERE r.patient_id = ?
        AND r.service_id IS NOT NULL
    `,
    params: [patientId, patientId],
    watch: [
      'dental_treatments',
      'dental_treatment_teeth',
      'dental_treatment_records',
      'dental_treatment_record_teeth',
      'appointment_services',
    ],
  };
}
